import { useTranslation } from 'react-i18next';
import { fetchBlobFromApi, downloadBlob } from '../../../api/apiClient';
import styles from './index.module.css';
import Title from './Title';

type Item = {
  key: string;
  value: string;
  href?: string;
};

type CardProps = {
  title: string;
  items: Item[];
  className?: string;
};

function safeSuggestedFilename(input: unknown, fallback = 'download'): string {
  // 0) Type guard
  if (typeof input !== 'string' || input.length === 0) return fallback;

  let raw = input;

  // 1) If a URL (including presigned), get just the pathname
  try {
    if (/^[a-z]+:\/\//i.test(raw) || /^\/\//.test(raw)) {
      // protocol-relative URLs get a dummy base
      const url = new URL(raw, 'http://example.local');
      raw = url.pathname; // still percent-encoded per spec
    }
  } catch {
    // ignore: treat as plain key
  }

  // 2) Take the last path segment and drop accidental ? / # if present in keys
  let last = raw.split('/').filter(Boolean).pop() ?? raw;
  last = last.split('?')[0].split('#')[0];

  // 3) Try to strip the UUID_ prefix (v1/v4), allowing multiple underscores after it
  //    Only strip if it actually matches the canonical UUID pattern.
  const uuidPrefix =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_+/i;
  let name = last.replace(uuidPrefix, '');

  // 4) Percent-decoding (guard against malformed sequences)
  try {
    name = decodeURIComponent(name);
  } catch {
    /* keep as-is */
  }

  // 5) Basic sanitization for filenames (browser will sanitize too, but be explicit)
  //    - remove directory separators and control chars
  //    - trim leading/trailing dots/spaces (Windows/macOS quirks)
  name = name
    .replace(/[\/\\:\x00-\x1f<>|"'*?]/g, '_')
    .replace(/^[.\s]+|[.\s]+$/g, '');

  // 6) Reasonable length cap, preserve extension if present
  if (name.length === 0) name = fallback;
  if (name.length > 200) {
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 && dot < name.length - 1 ? name.slice(dot) : '';
    name = name.slice(0, 200 - ext.length) + ext;
  }

  return name;
}

const ItemKey = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.itemKey}>{children}:</span>
);

const ItemValue = ({
  children,
  href,
}: {
  children: React.ReactNode;
  href?: string;
}) => {
  const { t } = useTranslation();
  const isDownloadable = !!href;

  const handleDownload = async (url: string, keyLike: unknown) => {
    try {
      const blob = await fetchBlobFromApi(url, {}, true);

      const filename = safeSuggestedFilename(keyLike);
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (isDownloadable) {
    return (
      <button
        onClick={() => handleDownload(href!, children)}
        className={`${styles.itemValue} ${styles.itemLink}`}
        type="button"
      >
        {t('reports.transaction-details-modal.download-file')}
      </button>
    );
  }
  return <span className={styles.itemValue}>{children}</span>;
};

const Card = ({ title, items, className = '' }: CardProps) => {
  return (
    <div className={`${styles.cardContainer} ${className}`}>
      <div className={styles.card}>
        <Title>{title}</Title>

        <section className={styles.cardContent}>
          {items.map(item => (
            <div key={item.key} className={styles.cardItem}>
              <ItemKey>{item.key}</ItemKey>
              <ItemValue href={item.href}>{item.value}</ItemValue>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default Card;
